import { getToken } from '../session/store';

const WS_HEARTBEAT_MS = 30000;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const DEBOUNCE_MS = 200;

let _socket = null;
let _queryClient = null;
let _token = null;
let _allowTokenless = false;
let _active = false;
let _connectAttempt = 0;
let _reconnectTimer = null;
let _heartbeatTimer = null;
let _listenersInstalled = false;
let _pendingByDeployment = new Map();
let _debounceTimers = new Map();

function buildWsUrl() {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/api/ws/events`;
}

export function startAppEventSocket(queryClient, { allowTokenless = false } = {}) {
  _queryClient = queryClient;
  const token = getToken();
  if (!token && !allowTokenless) {
    stopAppEventSocket();
    return;
  }
  _active = true;
  _allowTokenless = allowTokenless;
  _installRecoveryListeners();
  if (_token !== token) {
    _token = token;
    _forceReconnect();
    return;
  }
  _ensureConnected();
}

export function stopAppEventSocket() {
  _active = false;
  _token = null;
  _connectAttempt = 0;
  _allowTokenless = false;
  _clearReconnectTimer();
  _clearHeartbeatTimer();
  for (const timer of _debounceTimers.values()) clearTimeout(timer);
  _debounceTimers.clear();
  _pendingByDeployment.clear();
  if (_socket) {
    try {
      _socket.onopen = null;
      _socket.onmessage = null;
      _socket.onclose = null;
      _socket.onerror = null;
      _socket.close();
    } catch { /* ignore */ }
    _socket = null;
  }
}

function _ensureConnected() {
  if (!_active || (!_token && !_allowTokenless)) return;
  if (_socket && (_socket.readyState === WebSocket.OPEN || _socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  _connect();
}

function _connect() {
  _clearReconnectTimer();
  _clearHeartbeatTimer();
  const token = _token;
  if (!_active || (!token && !_allowTokenless)) return;
  try {
    const url = token ? `${buildWsUrl()}?token=${encodeURIComponent(token)}` : buildWsUrl();
    _socket = new WebSocket(url);
  } catch {
    _scheduleReconnect();
    return;
  }

  _socket.onopen = () => {
    _connectAttempt = 0;
    _startHeartbeat();
    _recoverMountedManualQueries();
  };

  _socket.onmessage = (event) => {
    let msg;
    try { msg = JSON.parse(event.data); } catch { return; }
    if (!msg || msg.version !== 1 || msg.type !== 'MANUAL_STATE_CHANGED') return;
    const deploymentId = Number(msg.deployment_id);
    const changes = Array.isArray(msg.changes) ? msg.changes : [];
    if (!Number.isFinite(deploymentId) || deploymentId <= 0 || changes.length === 0) return;
    _queueInvalidation(deploymentId, changes);
  };

  _socket.onclose = () => {
    _clearHeartbeatTimer();
    _socket = null;
    if (_active && _token === token) _scheduleReconnect();
  };

  _socket.onerror = () => {
    // onclose owns reconnect scheduling.
  };
}

function _forceReconnect() {
  _clearReconnectTimer();
  _clearHeartbeatTimer();
  if (_socket) {
    try {
      _socket.onclose = null;
      _socket.close();
    } catch { /* ignore */ }
    _socket = null;
  }
  _connect();
}

function _scheduleReconnect() {
  if (_reconnectTimer || !_active) return;
  _connectAttempt += 1;
  const base = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * (2 ** Math.min(_connectAttempt - 1, 5)));
  const jitter = Math.floor(Math.random() * Math.min(1000, base));
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    _connect();
  }, Math.min(RECONNECT_MAX_MS, base + jitter));
}

function _startHeartbeat() {
  _heartbeatTimer = setInterval(() => {
    if (_socket?.readyState === WebSocket.OPEN) {
      try { _socket.send(JSON.stringify({ action: 'ping' })); } catch { /* ignore */ }
    }
  }, WS_HEARTBEAT_MS);
}

function _clearReconnectTimer() {
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
}

function _clearHeartbeatTimer() {
  if (_heartbeatTimer) {
    clearInterval(_heartbeatTimer);
    _heartbeatTimer = null;
  }
}

function _queueInvalidation(deploymentId, changes) {
  const key = String(deploymentId);
  const pending = _pendingByDeployment.get(key) || new Set();
  for (const change of changes) pending.add(change);
  _pendingByDeployment.set(key, pending);
  if (_debounceTimers.has(key)) return;
  _debounceTimers.set(key, setTimeout(() => {
    _debounceTimers.delete(key);
    const flushed = _pendingByDeployment.get(key) || new Set();
    _pendingByDeployment.delete(key);
    _invalidateDeployment(deploymentId, flushed);
  }, DEBOUNCE_MS));
}

function _invalidateDeployment(deploymentId, changes) {
  if (!_queryClient) return;
  if (changes.has('orders')) {
    _queryClient.invalidateQueries({ queryKey: ['paperOrderbook', { deploymentId }] });
    _queryClient.invalidateQueries({ queryKey: ['paperOrderbook'] });
  }
  if (changes.has('positions')) {
    _queryClient.invalidateQueries({ queryKey: ['paperPositions', { deploymentId }] });
    _queryClient.invalidateQueries({ queryKey: ['paperPositions'] });
  }
  if (changes.has('risk_triggers')) {
    _queryClient.invalidateQueries({ queryKey: ['manualRiskTriggers', deploymentId] });
  }
  if (changes.has('trade_book')) {
    _queryClient.invalidateQueries({ queryKey: ['manualTradeBook', deploymentId], exact: false });
  }
  if (changes.has('watchlist')) {
    _queryClient.invalidateQueries({ queryKey: ['manualTradingWatchlist', deploymentId] });
  }
}

function _recoverMountedManualQueries() {
  if (!_queryClient) return;
  _queryClient.invalidateQueries({ queryKey: ['manualTradingDeployments'] });
  _queryClient.invalidateQueries({ queryKey: ['manualTradingWatchlist'], exact: false });
  _queryClient.invalidateQueries({ queryKey: ['manualRiskTriggers'], exact: false });
  _queryClient.invalidateQueries({ queryKey: ['manualTradeBook'], exact: false });
  _queryClient.invalidateQueries({ queryKey: ['paperOrderbook'], exact: false });
  _queryClient.invalidateQueries({ queryKey: ['paperPositions'], exact: false });
}

function _installRecoveryListeners() {
  if (_listenersInstalled || typeof window === 'undefined') return;
  _listenersInstalled = true;
  const onResume = () => {
    if (!_active) return;
    _ensureConnected();
    _recoverMountedManualQueries();
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onResume();
    });
  }
  window.addEventListener('focus', onResume);
  window.addEventListener('online', onResume);
  window.addEventListener('pageshow', onResume);
}
